import { Injectable } from '@angular/core';
import {
  BelpostContact,
  BelpostIndividualPerson,
  BelpostLegalPerson,
  BelpostPerson,
} from '$shared/types/belpost-contact';
import {
  WidgetContact,
  WidgetCountry,
  WidgetIndividualInfo,
  WidgetInternationalAddress,
  WidgetLegalInfo,
  WidgetPerson,
  WidgetRepublicAddress,
  WidgetRepublicAddressBaseInfo,
} from '$shared/components/address-widget-picker/model/address-widget-picker-contact';
import {
  BelpostCountry,
  BelpostRepublicAddress,
} from '$shared/types/belpost-republic-address';
import { BelpostInternationalAddress } from '$shared/types/belpost-international-address';
import { WidgetContactAddressType } from '../model/address-map-type';

/**
 * Адаптер для трансформации данных из BelpostContact в WidgetContact
 */
@Injectable({
  providedIn: 'root',
})
export class BelpostToWidgetUserAdapter {
  /**
   * Трансформирует BelpostContact в WidgetContact
   */
  public transform(
    belpostContact: BelpostContact | null
  ): WidgetContact | null {
    if (!belpostContact) {
      return null;
    }

    const result: WidgetContact = {
      person: belpostContact.person
        ? this.transformPerson(belpostContact.person)
        : null,
      address: belpostContact.address
        ? this.transformAddress(belpostContact.address)
        : null,
      isValid: belpostContact.isValid,
    };

    if (belpostContact.id !== null) {
      result.id = belpostContact.id;
    }

    return result;
  }

  /**
   * Трансформирует BelpostPerson в WidgetPerson
   */
  private transformPerson(belpostPerson: BelpostPerson): WidgetPerson {
    if (belpostPerson.type === 'individual') {
      return this.transformIndividualPerson(belpostPerson);
    } else {
      return this.transformLegalPerson(belpostPerson);
    }
  }

  /**
   * Трансформирует BelpostIndividualPerson в WidgetIndividualInfo
   */
  private transformIndividualPerson(
    belpostIndividualPerson: BelpostIndividualPerson
  ): WidgetIndividualInfo {
    return {
      type: 'individual',
      firstName: belpostIndividualPerson.first_name,
      lastName: belpostIndividualPerson.last_name,
      secondName: belpostIndividualPerson.second_name,
      email: belpostIndividualPerson.email,
      phone: belpostIndividualPerson.phone,
    };
  }

  /**
   * Трансформирует BelpostLegalPerson в WidgetLegalInfo
   */
  private transformLegalPerson(
    belpostLegalPerson: BelpostLegalPerson
  ): WidgetLegalInfo {
    return {
      type:
        belpostLegalPerson.type === 'individual_entrepreneur'
          ? 'individual-entrepreneur'
          : 'legal',
      organizationName: belpostLegalPerson.organization_name,
      email: belpostLegalPerson.email,
      phone: belpostLegalPerson.phone,
    };
  }

  /**
   * Трансформирует адрес из Belpost в Widget
   */
  private transformAddress(
    belpostAddress: BelpostRepublicAddress | BelpostInternationalAddress
  ): WidgetRepublicAddress | WidgetInternationalAddress {
    if (this.isBelpostRepublicAddress(belpostAddress)) {
      return this.transformRepublicAddress(belpostAddress);
    } else {
      return this.transformInternationalAddress(belpostAddress);
    }
  }

  /**
   * Проверяет, является ли адрес республиканским
   */
  private isBelpostRepublicAddress(
    address: BelpostRepublicAddress | BelpostInternationalAddress
  ): address is BelpostRepublicAddress {
    return 'external_id' in address;
  }

  /**
   * Трансформирует BelpostRepublicAddress в WidgetRepublicAddress
   */
  public transformRepublicAddress(
    belpostRepublicAddress: BelpostRepublicAddress
  ): WidgetRepublicAddress {
    const baseAddress: WidgetRepublicAddressBaseInfo = {
      house: belpostRepublicAddress.house,
      flat: belpostRepublicAddress.flat,
      block: belpostRepublicAddress.block,
      fullAddress: belpostRepublicAddress.full_address,
      externalId: belpostRepublicAddress.external_id,
      country: this.transformBelpostCountry(belpostRepublicAddress.country),
      city: belpostRepublicAddress.city,
      latitude: belpostRepublicAddress.latitude,
      longitude: belpostRepublicAddress.longitude,
      floor: belpostRepublicAddress.floor,
      entrance: belpostRepublicAddress.entrance,
      doorCode: belpostRepublicAddress.door_code,
      countryCode: belpostRepublicAddress.country_code,
    };

    if (belpostRepublicAddress.type === 'subscriber_box') {
      return {
        ...baseAddress,
        type: 'subscriber-box' as const,
        cellNumber: belpostRepublicAddress.cell_number
          ? String(belpostRepublicAddress.cell_number)
          : null,
      };
    }
    if (belpostRepublicAddress.type === 'on_demand') {
      return {
        ...baseAddress,
        type: 'on-demand' as const,
      };
    } else {
      return {
        ...baseAddress,
        type: 'address' as const,
      };
    }
  }

  /**
   * Трансформирует BelpostInternationalAddress в WidgetInternationalAddress
   */
  private transformInternationalAddress(
    belpostInternationalAddress: BelpostInternationalAddress
  ): WidgetInternationalAddress {
    return {
      type: 'address' as Extract<WidgetContactAddressType, 'address'>,
      country: this.transformBelpostCountry(
        belpostInternationalAddress.country
      ),
      postcode: belpostInternationalAddress.postcode,
      city: belpostInternationalAddress.city,
      addressLine: belpostInternationalAddress.address_line,
      flat: belpostInternationalAddress.flat,
      fullAddress: belpostInternationalAddress.full_address,
      latitude: belpostInternationalAddress.latitude
        ? String(belpostInternationalAddress.latitude)
        : null,
      longitude: belpostInternationalAddress.longitude
        ? String(belpostInternationalAddress.longitude)
        : null,
    };
  }

  /**
   * Трансформирует BelpostCountry в WidgetCountry
   */
  private transformBelpostCountry(
    belpostCountry: BelpostCountry
  ): WidgetCountry {
    return {
      code: belpostCountry.code,
      name: belpostCountry.name,
      isPopular: belpostCountry.is_popular,
      names: {
        ru: belpostCountry.names.ru,
        en: belpostCountry.names.en,
        be: belpostCountry.names.by,
      },
      availableLocales: {
        ru: belpostCountry.input_ru_lang,
        en: belpostCountry.input_en_lang,
      },
    };
  }
}
